import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container as SemanticContainer,
  Dimmer,
  Loader,
} from 'semantic-ui-react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/volto/registry';
import { NEWS_PUBLIC_STATES } from 'volto-ministerio-publico-federal/constants';
import {
  flattenHTMLToAppURL,
  flattenToAppURL,
  stripQuerystring,
} from '@plone/volto/helpers';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getContent } from '@plone/volto/actions/content/content';
import { usePagination } from '@plone/volto/helpers/Utils/usePagination';

import { LISTABLE_TYPES } from 'volto-ministerio-publico-federal/components/View/FolderView/listableTypes';
import PortalPagination from 'volto-ministerio-publico-federal/components/Pagination';
import { Attachments } from 'volto-ministerio-publico-federal/components/Attachments';

const normalizeQuery = (query) => {
  if (!query) return query;
  return query.map((item) => {
    if (item.i === 'portal_type' && item.v?.includes('Noticia')) {
      return {
        ...item,
        v: [...new Set([...item.v, 'News', 'News Item'])],
      };
    }
    return item;
  });
};

const ListingView = ({ content }) => {
  const dispatch = useDispatch();
  const querystringResults = useSelector(
    (state) => state.querystringsearch.subrequests,
  );

  const isCollection = content['@type'] === 'Collection';
  const hasQuery = isCollection && content.query?.length > 0;
  const subrequestID = content?.UID || 'listing';

  const { currentPage, setCurrentPage } = usePagination(subrequestID, 1);
  const folderPageSize = config.settings.defaultPageSize;
  const b_size = hasQuery ? content.item_count || 20 : folderPageSize;

  const folderBatch = useSelector(
    (state) => state.content.subrequests?.[subrequestID],
  );
  const isFolderPaged = !hasQuery && currentPage > 1;
  const folderBatchReady = isFolderPaged && folderBatch?.data?.['@id'];

  const isLoading = hasQuery
    ? querystringResults?.[subrequestID]?.loading
    : isFolderPaged && !folderBatchReady && !folderBatch?.error;

  const listingItems = hasQuery
    ? querystringResults?.[subrequestID]?.items || []
    : (isFolderPaged
        ? folderBatch?.data?.items?.filter((item) =>
            LISTABLE_TYPES.includes(item['@type']),
          )
        : content.items) || [];

  const total = hasQuery
    ? querystringResults?.[subrequestID]?.total || 0
    : content.items_total || 0;

  const totalPages = total > b_size ? Math.ceil(total / b_size) : 0;
  const batching = hasQuery
    ? querystringResults?.[subrequestID]?.batching
    : isFolderPaged
      ? folderBatch?.data?.batching
      : content.batching;

  const adaptedQuery = {
    b_size,
    query: normalizeQuery(content.query) || [],
    sort_on: content.sort_on || 'effective',
    sort_order: content.sort_reversed ? 'descending' : 'ascending',
    limit: content.limit || 0,
    metadata_fields: '_all',
  };

  const queryKey = hasQuery
    ? JSON.stringify([
        b_size,
        content.query,
        content.sort_on,
        content.sort_reversed,
        content.limit,
      ])
    : null;

  const cleanPath = stripQuerystring(flattenToAppURL(content['@id'])) || '/';

  useEffect(() => {
    if (hasQuery) {
      dispatch(
        getQueryStringResults(
          cleanPath,
          adaptedQuery,
          subrequestID,
          currentPage,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQuery, cleanPath, subrequestID, currentPage, queryKey]);

  useEffect(() => {
    if (!hasQuery && currentPage > 1) {
      dispatch(getContent(cleanPath, null, subrequestID, currentPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQuery, cleanPath, subrequestID, currentPage]);

  const blocks = content.blocks || {};
  const blocksLayout = content.blocks_layout?.items || [];

  const htmlBlocks = blocksLayout
    .map((id) => blocks[id])
    .filter((b) => b?.['@type'] === 'html');

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;
  const PreviewImage = config.getComponent({ name: 'PreviewImage' }).component;

  const listingRef = useRef();

  const renderItems = (items) => (
    <div className="mpf-content__editor-content" ref={listingRef}>
      {items.map((item) => (
        <div key={item.url || item['@id']} className="listing-item pl-2">
          <UniversalLink item={item} title={item.title}>
            {item.title}
          </UniversalLink>
          {item.description && <p>{item.description}</p>}
          {item.image_field && (
            <PreviewImage
              item={item}
              alt={item.preview_alt_link || item.image_caption || ''}
              className="ui image"
              responsive={true}
            />
          )}
        </div>
      ))}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <PortalPagination
            batching={batching || {}}
            currentPage={currentPage}
            totalItems={total}
            defaultPageSize={b_size}
            onPaginationChange={(e, { activePage }) => {
              listingRef.current?.scrollIntoView({ behavior: 'smooth' });
              setCurrentPage(activePage);
            }}
          />
        </div>
      )}
    </div>
  );

  if (htmlBlocks.length > 0) {
    return (
      <div id="page-listing-view" className="mpf-view-wrapper">
        {htmlBlocks.map((block, i) => (
          <div
            className="mpf-content__editor-content"
            key={i}
            dangerouslySetInnerHTML={{
              __html: flattenHTMLToAppURL(block.html),
            }}
          />
        ))}
      </div>
    );
  }

  if (isCollection) {
    return (
      <div id="page-listing-view" className="mpf-view-wrapper">
        {content.description && <p>{content.description}</p>}
        {isLoading ? (
          <div className="mpf-loading-content">
            <Dimmer active inverted>
              <Loader indeterminate size="small">
                Carregando...
              </Loader>
            </Dimmer>
          </div>
        ) : listingItems.length > 0 ? (
          renderItems(listingItems)
        ) : (
          <div className="mpf-content__editor-content" />
        )}
      </div>
    );
  }

  // Tudo o que está no ar, não só o Publicado Nacional: a notícia em
  // Publicado Local (`visible`), em revisão nacional ou com a nacional
  // revogada também é pública — filtrar por `published` a escondia da
  // listagem da pasta da unidade.
  const folderItems = (listingItems || []).filter((item) =>
    NEWS_PUBLIC_STATES.includes(item.review_state),
  );

  const fileItems = (listingItems || []).filter(
    (item) => item['@type'] === 'File',
  );

  return (
    <section id="content-core">
      {isLoading ? (
        <div className="mpf-loading-content">
          <Dimmer active inverted>
            <Loader indeterminate size="small">
              Carregando...
            </Loader>
          </Dimmer>
        </div>
      ) : fileItems.length > 0 ? (
        <Attachments items={fileItems} />
      ) : folderItems.length > 0 ? (
        renderItems(folderItems)
      ) : (
        <Container id="page-listing-view" className="mpf-view-wrapper">
          {content.description && <p>{content.description}</p>}

          <div className="mpf-content__editor-content">
            <div role="alert" className="mpf-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                className="stroke-info h-6 w-6 shrink-0"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Atualmente não existem itens nessa pasta.</span>
            </div>
          </div>
        </Container>
      )}
    </section>
  );
};

ListingView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    '@type': PropTypes.string,
    UID: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        description: PropTypes.string,
        review_state: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    items_total: PropTypes.number,
    item_count: PropTypes.number,
    query: PropTypes.array,
    sort_on: PropTypes.string,
    sort_reversed: PropTypes.bool,
    limit: PropTypes.number,
  }).isRequired,
};

export default ListingView;
